import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    GlobeAmericasIcon, ShieldCheckIcon, BoltIcon, TrophyIcon, 
    ArrowRightIcon, XMarkIcon, CheckCircleIcon, ShoppingBagIcon, ChartBarIcon,
    CpuChipIcon, BuildingLibraryIcon, CurrencyDollarIcon, BeakerIcon,
    NewspaperIcon, FilmIcon, HeartIcon, ScaleIcon, FireIcon, ClockIcon, LightBulbIcon, BookOpenIcon,
    UserGroupIcon, UsersIcon, GiftIcon, ShareIcon, ClipboardDocumentIcon, CheckIcon, MapPinIcon,
    ArrowsRightLeftIcon 
} from '@heroicons/react/24/solid';
import { useAuth } from '../hooks';
import { updateUserProfile } from '../api';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

// --- INTEREST OPTIONS ---
const INTEREST_OPTIONS = [
    { id: 'Politics', label: 'Politics', icon: BuildingLibraryIcon, color: 'text-red-500' },
    { id: 'Tech', label: 'Technology', icon: CpuChipIcon, color: 'text-blue-500' },
    { id: 'Environment', label: 'Nature', icon: GlobeAmericasIcon, color: 'text-green-500' },
    { id: 'Economics', label: 'Economics', icon: CurrencyDollarIcon, color: 'text-emerald-500' },
    { id: 'Science', label: 'Science', icon: BeakerIcon, color: 'text-purple-500' },
    { id: 'Law', label: 'Law & Justice', icon: ScaleIcon, color: 'text-indigo-500' },
    { id: 'Media', label: 'News & Media', icon: NewspaperIcon, color: 'text-slate-500' },
    { id: 'Culture', label: 'Culture', icon: FilmIcon, color: 'text-pink-500' },
    { id: 'Healthcare', label: 'Health', icon: HeartIcon, color: 'text-rose-500' },
    { id: 'Sports', label: 'Sports', icon: TrophyIcon, color: 'text-yellow-500' },
    { id: 'History', label: 'History', icon: ClockIcon, color: 'text-amber-600' },
    { id: 'Philosophy', label: 'Philosophy', icon: LightBulbIcon, color: 'text-cyan-500' }
];

// --- MECHANICS TIPS ---
const MECHANICS_TIPS = [
    {
        title: "Reaction Bombs",
        icon: FireIcon,
        color: "text-red-500",
        desc: "Deploy Logic (Gavel), Evidence (Book), or Insight (Cap) bombs to reward quality arguments. Grants Rep to the recipient and a small amount of XP to you."
    },
    {
        title: "2x XP Boost",
        icon: BoltIcon,
        color: "text-yellow-500",
        desc: "Activate boosts from your Sidebar/Profile. They last 24h and double all XP earned from posting arguments."
    },
    {
        title: "Earning Rep & XP",
        icon: TrophyIcon,
        color: "text-orange-500",
        desc: "Post arguments, win matches, and get upvoted. High AI scores grant bonus Reputation. Level up to unlock titles."
    },
    {
        title: "Houses & Verbies",
        icon: UserGroupIcon,
        color: "text-blue-500",
        desc: "Go to 'Groups' to join a House (Clan) or create a Verbie (Community). Houses compete for territory on the Global Map."
    },
    {
        title: "Training Sim",
        icon: CpuChipIcon,
        color: "text-purple-500",
        desc: "Sharpen your skills against the AI Grandmaster on the Home screen before facing human opponents."
    },
    {
        title: "10v1 Warzone",
        icon: FireIcon,
        color: "text-orange-500",
        desc: "Defend solo against 10 challengers or team up to storm the gates. High risk, massive Rep rewards."
    }
];

const TOUR_STEPS = [
    {
        id: 'intro',
        path: '/',
        title: "Welcome to Verbi",
        content: "You have entered the arena of intellectual combat. Here, logic is your weapon and evidence is your shield. Let's get you briefed on the tactical systems before you begin.",
        icon: TrophyIcon,
        color: "text-yellow-500",
        button: "Initialize Briefing"
    },
    {
        id: 'interests',
        path: '/',
        title: "Build Your Intel Profile",
        content: "Select the topics you are most knowledgeable in. The algorithm will prioritize these sectors for your 'For You' feed.",
        icon: CpuChipIcon,
        color: "text-blue-500",
        button: "Confirm Profile",
        isInteractive: true 
    },
    {
        id: 'feed_control', 
        path: '/',
        title: "Curate Your Feed",
        content: "Take control of your intelligence stream. Swipe RIGHT on a topic to Hide it. Swipe LEFT to Pin it to the top of your list.",
        icon: ArrowsRightLeftIcon,
        color: "text-indigo-500",
        button: "Got it"
    },
    {
        id: 'mechanics',
        path: '/',
        title: "Field Manual",
        content: "Master the tools of the trade. Review these essential protocols to survive the arena.",
        icon: BookOpenIcon,
        color: "text-slate-700",
        button: "Understood",
        isInteractive: true
    },
    {
        id: 'conquest',
        path: '/map',
        title: "Global Conquest",
        content: "This is the live war room. Regions marked in RED are active 'Hotzones'. Post arguments in these zones to capture territory for your House. Dominating regions grants massive House Reputation.",
        icon: GlobeAmericasIcon,
        color: "text-blue-500",
        button: "Next: Factions"
    },
    {
        id: 'bounties',
        path: '/communities',
        title: "House Operations",
        content: "Join a 'House' (Clan) to access the Bounty Board. Complete cooperative weekly quests—like 'The Logician' or 'The Empiricist'—to unlock the House Chest and earn XP boosts for your entire team.",
        icon: ShieldCheckIcon,
        color: "text-green-500",
        button: "Next: Logistics"
    },
    {
        id: 'store',
        path: '/store',
        title: "The Armory",
        content: "Visit the Marketplace to acquire 'Reaction Bombs'. Use them to reward wonderful Logic, Evidence, or Insight. This grants Rep to the recipient and a small amount of XP to you.",
        icon: ShoppingBagIcon,
        color: "text-purple-500",
        button: "Next: Intelligence"
    },
    {
        id: 'analytics',
        path: '/analytics',
        title: "Combat Intel",
        content: "Your personal dashboard. Here, the AI analyzes your 'Debate DNA'—breaking down your Logic, Evidence, and Civility scores over time. Use this data to refine your rhetorical style.",
        icon: ChartBarIcon,
        color: "text-indigo-500",
        button: "Next: The Arena"
    },
    {
        id: 'arena',
        path: '/',
        title: "Enter the Arena",
        content: "You are ready. Use 'Quick Match' for 3v3 battles or 'Training Sim' to spar against the AI Grandmaster. Your goal: Climb the ranks from Iron to Legend.",
        icon: BoltIcon,
        color: "text-red-500",
        button: "Next: 10v1 Mode"
    },
    {
        id: 'arena_10v1',
        path: '/arena10v1-lobby',
        title: "10v1 Warzone",
        content: "The ultimate test. One Defender. Ten Challengers. In this high-stakes mode, you can hold the line solo or join a squad to storm the gates. Winners earn massive Rep and bragging rights.",
        icon: FireIcon,
        color: "text-orange-500",
        button: "Final Step"
    },
    {
        id: 'referral',
        path: '/',
        title: "Recruitment Protocol",
        content: "Verbi is built for rivalry. Share your unique Handle as a recruitment code. When a recruit uses it, BOTH of you receive a randomized Loot Crate.",
        icon: UsersIcon,
        color: "text-indigo-500",
        button: "Complete Training",
        isInteractive: true
    }
];

const OnboardingTour = ({ isActive, onComplete }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    
    // State for Interests Selection
    const [selectedInterests, setSelectedInterests] = useState([]);

    // Sync visibility with prop
    useEffect(() => {
        if (isActive) {
            setIsVisible(true);
            setCurrentStepIndex(0);
        } else {
            setIsVisible(false);
        }
    }, [isActive]);

    // Handle Navigation Effect
    useEffect(() => {
        if (isActive && isVisible) {
            const step = TOUR_STEPS[currentStepIndex];
            if (step && step.path) {
                navigate(step.path);
            }
        }
    }, [currentStepIndex, isActive, isVisible, navigate]);

    const toggleInterest = (id) => {
        setSelectedInterests(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleShare = async () => {
        if (!currentUser) return;
        
        if (Capacitor.isNativePlatform()) {
            await Share.share({
                title: 'Verbi Recruitment',
                text: `I'm recruiting for my House on Verbi. Use my code "${currentUser.handle}" to get a free Loot Crate (XP Boosts & Bombs)!`,
                dialogTitle: 'Share Recruitment Code',
            });
        } else {
            navigator.clipboard.writeText(`Join Verbi with code: ${currentUser.handle}`);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleNext = async () => {
        const step = TOUR_STEPS[currentStepIndex];

        // SAVE INTERESTS if on that step
        if (step.id === 'interests' && currentUser) {
            try {
                // Save to profile without blocking the UI too much
                updateUserProfile(currentUser.uid, { interests: selectedInterests });
            } catch (e) {
                console.error("Failed to save interests:", e);
            }
        }

        if (currentStepIndex < TOUR_STEPS.length - 1) {
            setCurrentStepIndex(prev => prev + 1);
        } else {
            handleClose();
        }
    };

    const handleClose = () => {
        setIsVisible(false);
        if (onComplete) onComplete();
    };

    if (!isVisible) return null;

    const step = TOUR_STEPS[currentStepIndex];
    const Icon = step.icon;
    const progress = ((currentStepIndex + 1) / TOUR_STEPS.length) * 100;

    return (
        <div 
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
            style={{ 
                paddingTop: 'env(safe-area-inset-top)', 
                paddingBottom: 'env(safe-area-inset-bottom)' 
            }}
        >
            {/* Card Container */}
            <div className="bg-white w-full max-w-xl mx-auto rounded-3xl shadow-2xl overflow-hidden relative animate-slide-up border-2 border-slate-900 max-h-[85vh] flex flex-col">
                
                {/* Progress Bar */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100 z-10">
                    <div 
                        className="h-full bg-yellow-500 transition-all duration-500 ease-out" 
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                {/* Close Button */}
                <button 
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-900 transition-colors z-20 hover:bg-slate-100 rounded-full"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>

                <div className="p-6 md:p-8 flex flex-col items-center text-center overflow-y-auto custom-scrollbar">
                    
                    {/* Icon Halo */}
                    <div className={`w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center border-2 border-slate-200 shadow-sm mb-6 flex-shrink-0`}>
                        <Icon className={`w-10 h-10 ${step.color}`} />
                    </div>

                    <div className="w-full">
                        <h2 className="text-2xl font-black text-slate-900 mb-2 uppercase tracking-tight">
                            {step.title}
                        </h2>
                        
                        <div className="flex justify-center mb-6">
                            <span className="text-[10px] bg-slate-900 text-white px-2 py-0.5 rounded-full font-bold tracking-wider">
                                STEP {currentStepIndex + 1} OF {TOUR_STEPS.length}
                            </span>
                        </div>

                        <p className="text-slate-600 font-medium leading-relaxed text-sm mb-8 max-w-md mx-auto">
                            {step.content}
                        </p>

                        {/* --- INTERESTS GRID --- */}
                        {step.id === 'interests' && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8 max-h-60 overflow-y-auto pr-1 custom-scrollbar text-left">
                                {INTEREST_OPTIONS.map((opt) => {
                                    const isSelected = selectedInterests.includes(opt.id);
                                    const OptionIcon = opt.icon;
                                    return (
                                        <button
                                            key={opt.id}
                                            onClick={() => toggleInterest(opt.id)}
                                            className={`flex items-center p-3 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'bg-slate-900 border-slate-900 text-white shadow-md' : 'bg-white border-slate-100 text-slate-500 hover:border-slate-300'}`}
                                        >
                                            <OptionIcon className={`w-5 h-5 mr-2 ${isSelected ? 'text-yellow-400' : opt.color}`} />
                                            <span className="text-[10px] font-bold uppercase tracking-wide leading-tight">{opt.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* --- MECHANICS FIELD MANUAL --- */}
                        {step.id === 'mechanics' && (
                             <div className="space-y-3 mb-8 max-h-60 overflow-y-auto pr-1 custom-scrollbar text-left">
                                 {MECHANICS_TIPS.map((tip, idx) => {
                                     const TipIcon = tip.icon;
                                     return (
                                         <div key={idx} className="flex items-start p-3 bg-slate-50 rounded-xl border border-slate-100">
                                             <div className={`flex-shrink-0 p-1.5 rounded-lg bg-white border border-slate-200 mr-3 shadow-sm`}>
                                                 <TipIcon className={`w-5 h-5 ${tip.color}`} />
                                             </div>
                                             <div>
                                                 <h4 className="text-xs font-black text-slate-900 uppercase tracking-wide mb-0.5">{tip.title}</h4>
                                                 <p className="text-[11px] text-slate-500 leading-snug font-medium">{tip.desc}</p>
                                             </div>
                                         </div>
                                     );
                                 })}
                             </div>
                        )}
                        
                        {/* --- REFERRAL PROTOCOL (NEW) --- */}
                        {step.id === 'referral' && (
                            <div className="text-left mb-8 space-y-6">
                                {/* Benefits Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <UserGroupIcon className="w-5 h-5 text-blue-500 mb-2" />
                                        <h4 className="text-xs font-black text-slate-900 uppercase">Houses</h4>
                                        <p className="text-[10px] text-slate-500 leading-tight">Co-op conquest & bounties with your crew.</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <BoltIcon className="w-5 h-5 text-red-500 mb-2" />
                                        <h4 className="text-xs font-black text-slate-900 uppercase">PVP Arena</h4>
                                        <p className="text-[10px] text-slate-500 leading-tight">Host private matches and settle disputes.</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <UsersIcon className="w-5 h-5 text-green-500 mb-2" />
                                        <h4 className="text-xs font-black text-slate-900 uppercase">Intel Feed</h4>
                                        <p className="text-[10px] text-slate-500 leading-tight">Track their debates in your following feed.</p>
                                    </div>
                                    <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                                        <GiftIcon className="w-5 h-5 text-yellow-600 mb-2" />
                                        <h4 className="text-xs font-black text-yellow-800 uppercase">Free Loot</h4>
                                        <p className="text-[10px] text-yellow-700 leading-tight">You BOTH get a random Crate (Bombs/XP) on sign-up!</p>
                                    </div>
                                </div>

                                {/* Code Share Card */}
                                <div 
                                    onClick={handleShare}
                                    className="bg-slate-900 text-white p-4 rounded-xl shadow-lg cursor-pointer hover:bg-slate-800 transition-all active:scale-95 group relative overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full pointer-events-none"></div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Your Referral Code</p>
                                    <div className="flex justify-between items-end">
                                        <span className="text-2xl font-black tracking-wider text-yellow-400">{currentUser?.handle || "LOADING..."}</span>
                                        <div className="flex items-center text-xs font-bold bg-white/10 px-3 py-1.5 rounded-lg">
                                            {copySuccess ? <CheckIcon className="w-4 h-4 mr-1.5 text-green-400" /> : <ShareIcon className="w-4 h-4 mr-1.5" />}
                                            {copySuccess ? "Copied!" : "Share"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Button */}
                        <div className="w-full flex justify-center mt-auto">
                            <button
                                onClick={handleNext}
                                className="w-full sm:w-auto min-w-[200px] bg-slate-900 text-white font-bold py-4 px-8 rounded-xl uppercase tracking-widest hover:bg-yellow-500 hover:text-slate-900 transition-all shadow-lg flex items-center justify-center group text-xs md:text-sm"
                            >
                                <span>{step.button}</span>
                                {currentStepIndex < TOUR_STEPS.length - 1 ? (
                                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                ) : (
                                    <CheckCircleIcon className="w-4 h-4 ml-2" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingTour;